/* ==========================================================================
   Black Mango — Cart Drawer
   Slide-out cart with Section Rendering API integration.
   Vanilla JS, no dependencies. Transitions handled by CSS.
   ========================================================================== */

(function () {
  'use strict';

  class CartDrawer {
    constructor() {
      this.drawer = document.getElementById('CartDrawer');
      if (!this.drawer) return;

      this.overlay = document.querySelector('[data-cart-drawer-overlay]');
      this.content = this.drawer.querySelector('.cart-drawer__content');
      this.previousFocus = null;
      this.isOpen = false;

      this.#bindEvents();
    }

    /* ---- Public API ---------------------------------------------------- */

    open() {
      if (!this.drawer || this.isOpen) return;
      this.isOpen = true;
      this.previousFocus = document.activeElement;

      this.drawer.classList.add('cart-drawer--open');
      this.drawer.setAttribute('aria-hidden', 'false');
      if (this.overlay) this.overlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      if (window.BlackMangoLenis) window.BlackMangoLenis.stop();

      requestAnimationFrame(() => {
        const focusable = this.drawer.querySelector(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable) focusable.focus();
      });

      this.#trapFocus();
    }

    close() {
      if (!this.drawer || !this.isOpen) return;
      this.isOpen = false;

      this.drawer.classList.remove('cart-drawer--open');
      this.drawer.setAttribute('aria-hidden', 'true');
      if (this.overlay) this.overlay.classList.remove('is-open');
      document.body.style.overflow = '';
      if (window.BlackMangoLenis) window.BlackMangoLenis.start();

      this.#releaseFocus();

      if (this.previousFocus && typeof this.previousFocus.focus === 'function') {
        this.previousFocus.focus();
      }
      this.previousFocus = null;
    }

    toggle() {
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
    }

    async updateCart() {
      if (!this.drawer) return;

      try {
        const response = await fetch('?sections=cart-drawer', {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });

        if (!response.ok) throw new Error('Failed to fetch cart sections');

        const data = await response.json();
        const html = data['cart-drawer'];

        if (html) {
          const temp = document.createElement('div');
          temp.innerHTML = html;

          const newBody = temp.querySelector('.cart-drawer__body');
          const newFooter = temp.querySelector('.cart-drawer__footer');
          const currentBody = this.drawer.querySelector('.cart-drawer__body');
          const currentFooter = this.drawer.querySelector('.cart-drawer__footer');

          if (newBody && currentBody) currentBody.innerHTML = newBody.innerHTML;
          if (newFooter && currentFooter) {
            currentFooter.innerHTML = newFooter.innerHTML;
          } else if (newFooter && !currentFooter) {
            this.drawer.appendChild(newFooter.cloneNode(true));
          }

          this.#bindItemEvents();
        }
      } catch (err) {
        this.#showError('Could not update cart. Please refresh the page.');
        console.error('[BlackMango Cart]', err);
      }
    }

    async addToCart(formData) {
      if (!this.drawer) return;

      try {
        const response = await fetch(
          (window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/') + 'cart/add.js',
          {
            method: 'POST',
            body: formData,
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
          }
        );

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.description || 'Could not add item to cart');
        }

        /* Open drawer and update content together */
        this.open();
        document.dispatchEvent(new CustomEvent('cart:updated'));
        await this.updateCart();
      } catch (err) {
        this.#showError(err.message);
        console.error('[BlackMango Cart]', err);
      }
    }

    async updateQuantity(line, quantity) {
      if (!this.drawer) return;

      if (this._quantityAbort) {
        this._quantityAbort.abort();
      }
      clearTimeout(this._quantityDebounce);

      const controller = new AbortController();
      this._quantityAbort = controller;

      this.#setLoading(true);

      await new Promise((resolve) => {
        this._quantityDebounce = setTimeout(resolve, 50);
      });

      if (controller.signal.aborted) return;

      try {
        const response = await fetch(
          (window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/') + 'cart/change.js',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({ line: line, quantity: quantity }),
            signal: controller.signal,
          }
        );

        if (!response.ok) throw new Error('Failed to update quantity');

        await this.updateCart();
        document.dispatchEvent(new CustomEvent('cart:updated'));
      } catch (err) {
        if (err.name === 'AbortError') return;
        this.#showError('Could not update quantity. Please try again.');
        console.error('[BlackMango Cart]', err);
      } finally {
        if (this._quantityAbort === controller) {
          this._quantityAbort = null;
          this.#setLoading(false);
        }
      }
    }

    /* ---- Private methods ----------------------------------------------- */

    #bindEvents() {
      /* Toggle triggers (header cart icon) */
      const toggles = document.querySelectorAll('[data-cart-toggle]');
      for (const trigger of toggles) {
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          this.toggle();
        });
      }

      /* Close buttons */
      const closeBtns = document.querySelectorAll('[data-cart-drawer-close]');
      for (const btn of closeBtns) {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.close();
        });
      }

      /* Overlay click */
      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.close());
      }

      /* Escape key */
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.close();
        }
      });

      /* Bind item-level events */
      this.#bindItemEvents();
    }

    #bindItemEvents() {
      if (!this.drawer) return;

      /* Quantity buttons */
      const qtyBtns = this.drawer.querySelectorAll('[data-quantity-change]');
      for (const btn of qtyBtns) {
        btn.addEventListener('click', (e) => {
          const variantId = btn.dataset.quantityChange;
          const direction = btn.dataset.direction;
          const input = this.drawer.querySelector('[data-quantity-input="' + variantId + '"]');
          if (!input) return;

          let qty = parseInt(input.value, 10) || 1;
          if (direction === 'minus') qty = Math.max(0, qty - 1);
          else qty = qty + 1;

          input.value = qty;

          /* Find line index */
          const items = this.drawer.querySelectorAll('[data-cart-item]');
          let line = 1;
          for (const item of items) {
            if (item.dataset.variantId === variantId) break;
            line++;
          }

          this.updateQuantity(line, qty);
        });
      }

      /* Remove buttons */
      const removeBtns = this.drawer.querySelectorAll('[data-cart-remove]');
      for (const btn of removeBtns) {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const variantId = btn.dataset.cartRemove;

          const items = this.drawer.querySelectorAll('[data-cart-item]');
          let line = 1;
          for (const item of items) {
            if (item.dataset.variantId === variantId) break;
            line++;
          }

          /* Animate item out */
          const itemEl = btn.closest('[data-cart-item]');
          if (itemEl) {
            itemEl.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
            itemEl.style.opacity = '0';
            itemEl.style.transform = 'translateX(20px)';
          }

          setTimeout(() => {
            this.updateQuantity(line, 0);
          }, 300);
        });
      }
    }

    #trapFocus() {
      this._focusTrapHandler = (e) => {
        if (e.key !== 'Tab' || !this.isOpen) return;

        const focusables = this.drawer.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusables.length === 0) return;

        const first = focusables[0];
        const last = focusables[focusables.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      };

      document.addEventListener('keydown', this._focusTrapHandler);
    }

    #releaseFocus() {
      if (this._focusTrapHandler) {
        document.removeEventListener('keydown', this._focusTrapHandler);
        this._focusTrapHandler = null;
      }
    }

    #setLoading(loading) {
      if (!this.drawer) return;

      const qtyBtns = this.drawer.querySelectorAll('.cart-drawer__qty-btn');
      for (const btn of qtyBtns) {
        btn.disabled = loading;
      }

      if (loading) {
        this.drawer.setAttribute('aria-busy', 'true');
      } else {
        this.drawer.removeAttribute('aria-busy');
      }
    }

    #showError(message) {
      if (!this.drawer) return;

      const existing = this.drawer.querySelector('.cart-drawer__error');
      if (existing) existing.remove();

      const error = document.createElement('div');
      error.className = 'cart-drawer__error';
      error.setAttribute('role', 'alert');
      error.textContent = message;

      const body = this.drawer.querySelector('.cart-drawer__body');
      if (body) body.prepend(error);

      setTimeout(() => {
        if (error.parentNode) error.remove();
      }, 5000);
    }
  }

  /* ---- Init ------------------------------------------------------------ */
  function init() {
    const cart = new CartDrawer();
    window.BlackMangoCart = cart;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
